import {
  Button,
  ButtonVariant,
  FormMessage,
  FormMessageVariant,
  Input,
  RadioToggle,
  Spinner,
  toast,
} from '@aus-platform/design-system';
import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useOrgDomains, useUpdateSSOEnforcement } from 'shared/api';
import type { SSOEnforcementConfig } from 'shared/api/organisations/type';
import { SSOEnforcementType } from 'shared/enums';
import { SectionHeader } from 'shared/components';
import {
  SSO_ENFORCEMENT_MESSAGES,
  SSO_ENFORCEMENT_OPTIONS,
} from '../../constants';

type SSOEnforcementEditProps = {
  currentConfig: SSOEnforcementConfig;
  onCancel: VoidFunction;
  onSaveSuccess: VoidFunction;
  onUnsavedChanges: (hasChanges: boolean) => void;
  onShowUnsavedBanner: VoidFunction;
};

export const SSOEnforcementEdit: React.FC<SSOEnforcementEditProps> = ({
  currentConfig,
  onCancel,
  onSaveSuccess,
  onUnsavedChanges,
  onShowUnsavedBanner,
}) => {
  // States.
  const queryClient = useQueryClient();
  const [selectedType, setSelectedType] = useState<SSOEnforcementType>(
    currentConfig.ssoEnforcementType,
  );
  const [selectedDomains, setSelectedDomains] = useState<string[]>(
    currentConfig.ssoEnforcedDomains,
  );

  // Hooks.
  const { data: domainsResponse, isLoading: isLoadingDomains } = useOrgDomains(
    selectedType === SSOEnforcementType.SelectedDomains,
  );

  const { mutate: updateEnforcement, isPending: isSaving } =
    useUpdateSSOEnforcement();

  // Derived state.
  const domainOptions = useMemo(() => {
    if (!domainsResponse?.data?.domains) {
      return [];
    }

    return domainsResponse.data.domains.map((domain) => ({
      label: domain.name,
      value: domain.name,
    }));
  }, [domainsResponse]);

  const domainsData = domainsResponse?.data?.domains ?? [];

  const inactiveDomains = useMemo(() => {
    return selectedDomains.filter((domain) => {
      const domainInfo = domainsData.find(
        (domainInfo) => domainInfo.name === domain,
      );
      return domainInfo && !domainInfo.hasActiveUsers;
    });
  }, [selectedDomains, domainsData]);

  const hasChanges = useMemo(() => {
    if (selectedType !== currentConfig.ssoEnforcementType) {
      return true;
    }

    if (selectedType === SSOEnforcementType.SelectedDomains) {
      const currentSorted = [...currentConfig.ssoEnforcedDomains].sort();
      const selectedSorted = [...selectedDomains].sort();
      return JSON.stringify(currentSorted) !== JSON.stringify(selectedSorted);
    }

    return false;
  }, [selectedType, selectedDomains, currentConfig]);

  const isSaveDisabled =
    !hasChanges ||
    isSaving ||
    (selectedType === SSOEnforcementType.SelectedDomains && selectedDomains.length === 0);

  useEffect(() => {
    onUnsavedChanges(hasChanges);
  }, [hasChanges]);

  // Handlers.
  const handleSave = () => {
    const payload: SSOEnforcementConfig = {
      ssoEnforcementType: selectedType,
      ssoEnforcedDomains:
        selectedType === SSOEnforcementType.SelectedDomains ? selectedDomains : [],
    };

    updateEnforcement(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['sso-enforcement'] });
        onUnsavedChanges(false);
        onSaveSuccess();
      },
      onError: () => {
        toast.error(SSO_ENFORCEMENT_MESSAGES.SaveError);
      },
    });
  };

  const handleBack = () => {
    if (hasChanges) {
      onShowUnsavedBanner();
      return;
    }
    onCancel();
  };

  const handleCancel = () => {
    setSelectedType(currentConfig.ssoEnforcementType);
    setSelectedDomains(currentConfig.ssoEnforcedDomains);
    onUnsavedChanges(false);
    onCancel();
  };

  const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.currentTarget.checked) {
      setSelectedType(event.currentTarget.value as SSOEnforcementType);
    }
  };

  const handleDomainChange = (
    selectedOptions: Array<{ label: string; value: string }> | null,
  ) => {
    setSelectedDomains(
      selectedOptions ? selectedOptions.map((option) => option.value) : [],
    );
  };

  const selectedDomainValues = selectedDomains.map((domain) => ({
    label: domain,
    value: domain,
  }));

  return (
    <div className="settings-content">
      <div className="settings-content__body org-controls-tab__edit">
        <SectionHeader
          backLabel="Single Sign-On Enforcement"
          description="Choose who must sign in using SSO. Others can continue using email and password."
          onBack={handleBack}
        />

        <div className="org-controls-tab__edit__options">
          {Object.entries(SSO_ENFORCEMENT_OPTIONS).map(([type, option]) => {
            const optionClassName = classNames(
              'org-controls-tab__edit__option',
              {
                'org-controls-tab__edit__option--selected':
                  selectedType === type,
              },
            );

            return (
            <div
              key={type}
              className={optionClassName}
            >
              <RadioToggle
                value={type}
                name="sso-enforcement-type"
                label={option.label}
                checked={selectedType === type}
                onChange={handleTypeChange}
                disabled={isSaving}
              />
              <p className="org-controls-tab__edit__option__description">
                {option.description}
              </p>

              {type === SSOEnforcementType.SelectedDomains &&
                selectedType === SSOEnforcementType.SelectedDomains && (
                  <div className="org-controls-tab__edit__domain-select">
                    {isLoadingDomains ? (
                      <Spinner />
                    ) : (
                      <Input.Select
                        placeholder="Choose Domains"
                        options={domainOptions}
                        isMulti={true}
                        value={selectedDomainValues}
                        onChange={handleDomainChange}
                        isDisabled={isSaving}
                      />
                    )}
                    {inactiveDomains.length > 0 && (
                      <FormMessage
                        showIcon
                        variant={FormMessageVariant.Warning}
                        className="org-controls-tab__edit__domain-warning"
                        message={SSO_ENFORCEMENT_MESSAGES.NoActiveUsers(
                          inactiveDomains,
                        )}
                      />
                    )}
                  </div>
                )}
            </div>
            );
          })}
        </div>

        {selectedType === SSOEnforcementType.SelectedDomains &&
          selectedDomains.length === 0 && (
            <FormMessage
              variant={FormMessageVariant.Info}
              message={SSO_ENFORCEMENT_MESSAGES.SelectDomainRequired}
            />
          )}
      </div>

      <div className="settings-content__footer">
        <Button
          variant={ButtonVariant.Secondary}
          onClick={handleCancel}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={isSaveDisabled}
          isLoading={isSaving}
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
};
